import React, { useState, useEffect, useCallback } from 'react';
import AnswerSubmission from './AnswerSubmission';
import StarRating from './StarRating';
import VideoPopup from './VideoPopup';
import LinkSection from './LinkSection';
import { isDayCompleted, getDayStars, getDaySubmissionData } from '../utils/completionStorage';
import { BadgeType, BadgeTier } from '../utils/badges';
import { marked } from 'marked';
import { sound } from '../utils/sound';

interface ContentFlags {
  response: boolean;
  promptUsed: boolean;
  files: boolean;
  transcript: boolean;
}

interface ContentLink {
  title: string;
  video_url: string;
}

interface DayContent {
  day: number;
  title: string;
  contentType: string;
  content: string | object;
  contentFlags?: ContentFlags;
  videos?: ContentLink[];
}

interface ScoreBreakdown {
  relevance: number;
  correctness: number;
  depth: number;
  clarity: number;
  promptcraft: number;
}

interface NewBadge {
  type: BadgeType;
  tier: BadgeTier;
}

interface BonusData {
  totalPoints: number;
  stars?: number;
  scoreBreakdown?: ScoreBreakdown;
  scoreDelta?: number;
  addedPoints?: number; // The actual points added to total score
  isImprovement?: boolean; // Whether this is an improvement over previous submission
  previousBreakdown?: ScoreBreakdown | null; // Previous breakdown for comparison
  newBadges?: NewBadge[]; // Newly earned badges
}

interface ChatSession {
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  conversationId: number | null;
}

interface DayModalProps {
  dayContent: DayContent | null;
  dayType?: string;
  companyGameId?: number;
  totalAvailableDays?: number;
  availableDays?: number[];
  onClose: () => void;
  isCurrentDay?: boolean;
  onServerAnswer?: (bonusData: BonusData) => void;
  onScoreAnimationStart?: (badges?: NewBadge[]) => void;
  shouldClose?: boolean;
  loading?: boolean;
  chatSession?: ChatSession;
  onChatSessionUpdate?: (session: ChatSession) => void;
  onChatSessionReset?: () => void;
}

const DayModal: React.FC<DayModalProps> = ({ dayContent, dayType, companyGameId, totalAvailableDays, availableDays, onClose, isCurrentDay = false, onServerAnswer, onScoreAnimationStart, shouldClose = false, loading = false, chatSession, onChatSessionUpdate, onChatSessionReset }) => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [wasAlreadyCompleted, setWasAlreadyCompleted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [contentClosed, setContentClosed] = useState(false);
  const [dayStars, setDayStars] = useState(0);
  const [showStoredFeedback, setShowStoredFeedback] = useState(false);
  const [storedSubmission, setStoredSubmission] = useState<any>(null);
  const [videoPopupUrl, setVideoPopupUrl] = useState<string | null>(null);

  // Check if day was already completed on mount
  useEffect(() => {
    if (!dayContent) return;

    const alreadyCompleted = isDayCompleted(dayContent.day);
    const stars = getDayStars(dayContent.day);
    const submissionData = getDaySubmissionData(dayContent.day);

    console.log(`[DayModal] Day ${dayContent.day} submission data:`, submissionData);

    setIsCompleted(alreadyCompleted);
    setWasAlreadyCompleted(alreadyCompleted);
    setDayStars(stars);
    setStoredSubmission(submissionData);
  }, [dayContent]);

  const handleClose = useCallback(() => {
    if (isClosing) return; // Prevent multiple close calls

    sound.clickClose();

    setIsClosing(true);

    // Wait for animation to complete before closing
    setTimeout(() => {
      setContentClosed(true);
      onClose();
      // Dispatch event to notify that modal is closed
      window.dispatchEvent(new CustomEvent('modalClosed'));
    }, 350); // Match the animation duration
  }, [isClosing, onClose]);

  // Watch for shouldClose prop change
  useEffect(() => {
    if (shouldClose) {
      handleClose();
    }
  }, [shouldClose, handleClose]);

  // Handle Escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isClosing) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isClosing, handleClose]);

  const handleLocalServerAnswer = (bonus: BonusData) => {
    console.log('DayModal: Received answer bonus data:', bonus);

    // Update local state
    setIsCompleted(bonus.stars !== undefined && bonus.stars > 0);
    if (bonus.stars !== undefined) {
      setDayStars(bonus.stars);
    }

    // Notify parent - App.tsx will show the score popup and close the modal
    if (onServerAnswer) {
      console.log('DayModal: Calling onServerAnswer');
      onServerAnswer(bonus);
    }
  };

  const handleVideoClick = (url: string) => {
    setVideoPopupUrl(url);
  };

  const renderContent = () => {
    if (!dayContent) return null;

    if (dayContent.contentType === 'json') {
      return (
        <div className="json-content">
          <pre>{JSON.stringify(dayContent.content, null, 2)}</pre>
        </div>
      );
    }

    // Check if content is markdown or HTML
    const content = dayContent.content as string;
    let htmlContent = content;

    // If contentType is markdown or content doesn't look like HTML, convert it
    if (dayContent.contentType === 'markdown' || (!content.startsWith('<') && content.includes('#'))) {
      // Normalize line breaks (convert \r\n to \n) before parsing
      const normalizedContent = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

      htmlContent = marked(normalizedContent) as string;
    }

    return (
      <div
        className="html-content"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  };

  // Determine if this is a special day type (requireRow or requireStars)
  const isSpecialDay = dayType === 'requireRow' || dayType === 'requireStars';

  return (
    <>
      <div className={`modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
        {!contentClosed && !loading && dayContent && (
          <div className={`modal-content ${isClosing ? 'closing' : ''} ${isSpecialDay ? 'special-day' : ''} modal-content-loaded`} onClick={(e) => e.stopPropagation()}>
            <div className={`modal-header ${isSpecialDay ? 'special-day' : ''}`}>
              <div className="modal-header-content">
                <h2>
                  {isCurrentDay && <span className="today-badge">TODAY</span>}
                  {dayContent.title}
                </h2>
              </div>
              <button className="modal-close-button" onClick={handleClose}>
                ✕
              </button>
            </div>
            {storedSubmission && storedSubmission.score !== undefined && (
              <button
                className={`score-frame ${isSpecialDay ? 'special-day' : ''}`}
                onClick={() => {
                  if (showStoredFeedback) {
                    sound.clickClose();
                    setShowStoredFeedback(false);
                  } else {
                    sound.clickOpen();
                    setShowStoredFeedback(true);
                  }
                }}
              >
                <div className="score-info">
                  <span className="modal-score-label">Best Score:</span>
                  <span className="modal-score-value">{storedSubmission.score}</span>
                  {dayStars > 0 && (
                    <div className="score-stars">
                      <StarRating stars={dayStars} animate={false} size="medium" useSvg={false} />
                    </div>
                  )}
                  <div className="feedback-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      <line x1="7" y1="8.5" x2="17" y2="8.5"></line>
                      <line x1="7" y1="12" x2="17" y2="12"></line>
                    </svg>
                  </div>
                </div>
              </button>
            )}
            <div className="modal-body">
              {!showStoredFeedback ? (
                <>
                  {renderContent()}
                  {dayContent.videos && dayContent.videos.length > 0 && (
                    <LinkSection videos={dayContent.videos} onVideoClick={handleVideoClick} />
                  )}
                  <AnswerSubmission
                    dayNumber={dayContent.day}
                    isCurrentDay={isCurrentDay}
                    isSpecialDay={isSpecialDay}
                    companyGameId={companyGameId}
                    totalAvailableDays={totalAvailableDays}
                    availableDays={availableDays}
                    onServerAnswer={handleLocalServerAnswer}
                    storedSubmission={storedSubmission}
                    contentFlags={dayContent.contentFlags}
                    chatSession={chatSession}
                    onChatSessionUpdate={onChatSessionUpdate}
                    onChatSessionReset={onChatSessionReset}
                  />
                </>
              ) : (
                <div className="inline-feedback-view">
                  {storedSubmission?.scoreBreakdown &&
                   storedSubmission.scoreBreakdown.relevance !== undefined &&
                   storedSubmission.scoreBreakdown.correctness !== undefined &&
                   storedSubmission.scoreBreakdown.depth !== undefined &&
                   storedSubmission.scoreBreakdown.clarity !== undefined &&
                   storedSubmission.scoreBreakdown.promptcraft !== undefined && (
                    <div className="inline-score-breakdown">
                      <div className="inline-score-column">
                        <div className="inline-score-label">Relevance</div>
                        <div className="inline-score-value">{storedSubmission.scoreBreakdown.relevance}%</div>
                      </div>
                      <div className="inline-score-column">
                        <div className="inline-score-label">Correctness</div>
                        <div className="inline-score-value">{storedSubmission.scoreBreakdown.correctness}%</div>
                      </div>
                      <div className="inline-score-column">
                        <div className="inline-score-label">Depth</div>
                        <div className="inline-score-value">{storedSubmission.scoreBreakdown.depth}%</div>
                      </div>
                      <div className="inline-score-column">
                        <div className="inline-score-label">Clarity</div>
                        <div className="inline-score-value">{storedSubmission.scoreBreakdown.clarity}%</div>
                      </div>
                      <div className="inline-score-column">
                        <div className="inline-score-label">Promptcraft</div>
                        <div className="inline-score-value">{storedSubmission.scoreBreakdown.promptcraft}%</div>
                      </div>
                    </div>
                  )}
                  {storedSubmission?.feedback && (
                    <div className="inline-feedback-section">
                      {storedSubmission.feedback?.summary && (
                        <div className={`inline-feedback-summary ${dayStars === 0 ? 'incomplete' : 'complete'}`}>
                          {storedSubmission.feedback.summary}
                        </div>
                      )}
                      {storedSubmission.feedback?.strengths && Array.isArray(storedSubmission.feedback.strengths) && storedSubmission.feedback.strengths.length > 0 && (
                        <div className="inline-feedback-group">
                          <div className="inline-feedback-title">Strengths</div>
                          <ul className="inline-feedback-list">
                            {storedSubmission.feedback.strengths.map((strength: string, index: number) => (
                              <li key={index}>{strength}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {storedSubmission.feedback?.improvements && Array.isArray(storedSubmission.feedback.improvements) && storedSubmission.feedback.improvements.length > 0 && (
                        <div className="inline-feedback-group">
                          <div className="inline-feedback-title">Areas for Improvement</div>
                          <ul className="inline-feedback-list">
                            {storedSubmission.feedback.improvements.map((improvement: string, index: number) => (
                              <li key={index}>{improvement}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="inline-feedback-actions">
                    <button
                      className={`close-feedback-button ${isSpecialDay ? 'special-day' : ''}`}
                      onClick={() => {
                        sound.clickClose();
                        setShowStoredFeedback(false);
                      }}
                    >
                      Close Feedback
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {videoPopupUrl && (
        <VideoPopup
          videoUrl={videoPopupUrl}
          onClose={() => setVideoPopupUrl(null)}
        />
      )}
    </>
  );
};

export default DayModal;
